import { nextInstanceId } from './instanceCounter';
import { ensureBookingLoader } from './facade';

/**
 * Directly initialise timum booking into a root element (no iframe wrapper).
 * Used by script-tag consumers and — internally — by the iframe srcdoc bootstrap.
 *
 * Lives in its own module (not index.jsx) so the minimal boot entry
 * (src/boot.js) can re-export it WITHOUT dragging in index.jsx's yup / i18next /
 * React re-exports. That keeps the iframe's srcdoc re-download tiny.
 *
 * As of the load-facade split, `init` is ASYNC: it dynamically imports the heavy
 * render path (App + Redux + MUI + FullCalendar) so the loading spinner can paint
 * first. It returns a promise that resolves once the app chunk has mounted.
 *
 * The recommended integrations (`initialise` / `TimumBooking`) call this inside
 * the iframe srcdoc with `await init(...)`, so a failed app-chunk download is
 * surfaced as an error message in place of the spinner. Direct (non-iframe)
 * callers get no such fallback UI, so they should attach a `.catch(...)` to
 * handle a failed chunk load:
 *   `init(appConfig).catch((e) => { ... });`
 *
 * @returns {Promise<void>}
 */
export async function init(appConfig, muiTheme, fcConfig, iFrameDocument) {
  let rootEl = iFrameDocument?.getElementById(
    appConfig?.rootElId ?? 'bookingjs',
  );

  if (!rootEl) {
    rootEl = document.getElementById(appConfig?.rootElId ?? 'bookingjs');
  }

  // Paint the loading spinner immediately so the direct (non-iframe) init path
  // isn't a blank white box during the app-chunk download — the same spinner the
  // iframe paths already show. No-op on the iframe path (its srcdoc already
  // painted one); `createRoot()` in mountBooking clears it on first commit.
  ensureBookingLoader(rootEl, {
    height: appConfig?.height,
    culture: appConfig?.culture,
  });

  // Each init() call gets its own Redux store and i18n instance so multiple
  // widgets on the same page don't share state.
  const instanceId = nextInstanceId('bookingjs');

  // Load the heavy render path (App + Redux + i18n + MUI + FullCalendar) on
  // demand. Keeping it behind this dynamic import is what keeps the entry chunk
  // small, so the loading spinner in the srcdoc paints *before* this multi-MB
  // chunk downloads — and so the chunk is fetched once (here, inside the iframe)
  // rather than once in the parent just to obtain `initialise`.
  const { mountBooking } = await import('./mountBooking');
  mountBooking({ rootEl, appConfig, muiTheme, fcConfig, instanceId });
}
